<script lang="ts" setup>
import { sleep } from '@antfu/utils';
import { CATALOG_PAGE_TYPES } from '~/constants/catalogPageTypes';
import type { Pagination } from '~/types/Pagination';
import { cloneValue } from '~/utils/cloneValue';
import type {
  ActiveFilter,
  CatalogPageData,
  Filter,
  ItemListSchema,
  OfferMedium,
  RecommendationsResponse,
  SortingOption,
} from '~/types/Catalog';
import { getSearchSortingOptions, getSelectedSortingOption } from '~/utils/search';

const props = defineProps<{
  activeFilters: ActiveFilter[];
  filters?: Filter[];
  offers: OfferMedium[];
  pagination?: Pagination;
  relevanceSort?: boolean;
  schema?: ItemListSchema;
  sortOptions?: SortingOption[];
}>();

const route = useRoute();
const catalogStore = useCatalogStore();
const siteStore = useSiteStore();
const selectedOffers = ref<Record<number, OfferMedium>>({});
const { catalogPageIsLoading } = storeToRefs(catalogStore);
const { isShowHeader } = storeToRefs(siteStore);
const availableSortOptions = computed(() =>
  props.relevanceSort ? getSearchSortingOptions(props.sortOptions) : (props.sortOptions ?? []),
);
const selectedSortOption = ref<SortingOption>(getSelectedSortingOption(availableSortOptions.value, route.query.sort));

function getCardOffer(offer: OfferMedium) {
  return selectedOffers.value[offer.id] ?? offer;
}

function setSelectedCardOffer(sourceOfferId: number, offer: OfferMedium) {
  selectedOffers.value[sourceOfferId] = offer;
}
const openFilters = ref(props.filters?.map((filter) => filter.slug));
const isShowMobileFilters = ref(false);
const controlsElement = ref<HTMLElement | null>(null);
const isControlsSticky = ref(false);
const { appUrl } = useRuntimeConfig().public;
const { isMobile } = useBreakpoints();
const combinedOffers = ref(props.offers);
const computedPagination = ref(props.pagination);

useJsonld(
  props.schema
    ? () =>
        ({
          ...getItemListSchemaWithSeoHeaders(props.schema as ItemListSchema, combinedOffers.value),
          url: `${appUrl}${route.fullPath}`,
        }) as any
    : null,
);

async function changeSort(newSortOption: SortingOption) {
  selectedSortOption.value = newSortOption;
  const query = { ...route.query };

  delete query.page;

  if (newSortOption.value) {
    query.sort = newSortOption.value;
  } else {
    delete query.sort;
  }

  await navigateTo({
    path: route.path,
    query,
  });
}

const { sendPageViewOffersEventToDataLayer } = useGtmModule();

function sendViewedOffersToDataLayer(offers?: OfferMedium[]) {
  if (import.meta.client) sendPageViewOffersEventToDataLayer('view_item_list', offers || props.offers);
}

watch(
  () => route.query.page,
  async () => {
    await sleep(3000);
    sendViewedOffersToDataLayer();
  },
);

async function removeFilter(filter: ActiveFilter) {
  const query = cloneValue(route.query);
  const filterSlug = `filter[${filter.filterSlug}]`;

  if (filter.valueSlug) {
    const filterValues = query[filterSlug];

    if (typeof filterValues === 'string') {
      const filteredValues = filterValues
        .split(';')
        .filter((value) => value !== filter.valueSlug)
        .join(';');

      if (filteredValues) {
        query[filterSlug] = filteredValues;
      } else {
        delete query[filterSlug];
      }
    }
  } else {
    delete query[filterSlug];
  }

  await navigateTo({
    path: route.path,
    query,
  });
}

const { sendOfferEventToDataLayer } = useGtmModule();

async function clearFilters() {
  selectedSortOption.value = getSelectedSortingOption(availableSortOptions.value, undefined);

  if (route.query['filter[search]']) {
    await navigateTo({ path: route.path, query: { 'filter[search]': route.query['filter[search]'] } });
  } else {
    await navigateTo({ path: route.path, query: {} });
  }
}

function switchFiltersIsOpenState() {
  if (openFilters.value?.length) {
    openFilters.value = [];
  } else {
    openFilters.value = props.filters?.map((filter) => filter.slug);
  }
}

watch(
  () => props.offers,
  () => (combinedOffers.value = props.offers),
);
watch(
  () => props.pagination,
  () => (computedPagination.value = props.pagination),
);
watch(
  () => route.query.sort,
  (sort) => {
    selectedSortOption.value = getSelectedSortingOption(availableSortOptions.value, sort);
  },
);

const pagesCount = computed(() =>
  computedPagination.value ? Math.ceil(computedPagination.value.total / computedPagination.value.per_page) : 0,
);
const isLastPage = computed(() => computedPagination.value?.page === pagesCount.value);

async function loadMore() {
  if (!computedPagination.value || isLastPage.value) {
    return;
  }

  const page = String(computedPagination.value.page + 1);

  window.history.replaceState(
    window.history.state,
    '',
    `${route.path}?${new URLSearchParams({ ...route.query, page }).toString()}`,
  );
  route.query.page = page;

  let data;

  switch (catalogStore.currentPageType) {
    case CATALOG_PAGE_TYPES.CATEGORY_PAGE:
    case CATALOG_PAGE_TYPES.SEO_CATEGORY_PAGE:
      data = await catalogStore.getCatalogData();
      break;
    case CATALOG_PAGE_TYPES.SEARCH:
      data = await catalogStore.getSearchResults();
      break;
    case CATALOG_PAGE_TYPES.FAVORITE:
      data = await catalogStore.getFavorites();
      break;
    case CATALOG_PAGE_TYPES.BRAND:
      data = await catalogStore.getBrandBySlug();
      break;
    case CATALOG_PAGE_TYPES.SALE:
      data = await catalogStore.getSale();
      break;
    case CATALOG_PAGE_TYPES.SALE_COLLECTION:
      data = await catalogStore.getSaleCollection();
      break;
    case CATALOG_PAGE_TYPES.NEW:
      data = await catalogStore.getNew();
      break;
    default:
      break;
  }

  if (data?.data) {
    const catalogPageData = data?.data as CatalogPageData;

    computedPagination.value = catalogPageData.pagination;
    combinedOffers.value = [...combinedOffers.value, ...catalogPageData.offers];

    await nextTick();
    sendViewedOffersToDataLayer(catalogPageData.offers);

    if (window?.ym) {
      const { yandexMetrikaId } = useRuntimeConfig().public;
      window.ym(yandexMetrikaId, 'hit', window.location.href);
    }
  }
}

const productsCountPluralized = computed(() => {
  const count = computedPagination.value?.total ?? combinedOffers.value.length ?? 0;

  return `${count} ${getNoun(count, 'товар', 'товара', 'товаров')}`;
});

const recommendationsBlock = ref<RecommendationsResponse>();
const recommendedOffers = ref<OfferMedium[]>();
const isNoOffers = computed(() => props.offers.length === 0 && props.activeFilters.length === 0);
let controlsStickyFrameId: number | null = null;

function updateControlsStickyState() {
  if (!controlsElement.value || window.innerWidth >= 1024) {
    isControlsSticky.value = false;

    return;
  }

  const stickyTop = Number.parseFloat(getComputedStyle(controlsElement.value).top);

  isControlsSticky.value = controlsElement.value.getBoundingClientRect().top <= stickyTop + 1;
}

function scheduleControlsStickyStateUpdate() {
  if (controlsStickyFrameId !== null) return;

  controlsStickyFrameId = requestAnimationFrame(() => {
    controlsStickyFrameId = null;
    updateControlsStickyState();
  });
}

watch(isShowHeader, () => nextTick(scheduleControlsStickyStateUpdate));

onMounted(() => {
  if (import.meta.client && window.r46 && isNoOffers.value) {
    for (const block of siteStore.recommendationBlocks) {
      if (block.type === 'page_without_offers') {
        window.r46('recommend', block.block_identifier, {}, async function (response: RecommendationsResponse) {
          if (!response.recommends.length) return;

          recommendationsBlock.value = { ...response, block_identifier: block.block_identifier };
          recommendedOffers.value = await catalogStore.getOffersList(response.recommends.join(','));
        });
      }
    }
  }

  sendViewedOffersToDataLayer();
  updateControlsStickyState();
  window.addEventListener('scroll', scheduleControlsStickyStateUpdate, { passive: true });
  window.addEventListener('resize', scheduleControlsStickyStateUpdate);
});

onBeforeUnmount(() => {
  window.removeEventListener('scroll', scheduleControlsStickyStateUpdate);
  window.removeEventListener('resize', scheduleControlsStickyStateUpdate);

  if (controlsStickyFrameId !== null) cancelAnimationFrame(controlsStickyFrameId);
});
</script>

<template>
  <div class="products-listing">
    <section
      v-if="filters?.length && !isNoOffers"
      class="products-listing__filters"
    >
      <div class="flex items-center mt-2">
        {{ productsCountPluralized }}
      </div>

      <div class="flex flex-col rounded-xl p-4 gap-4 bg-white shadow-xl">
        <button @click="switchFiltersIsOpenState">
          <h3 class="flex items-center justify-between gap-2">
            Фильтры

            <SvgoChevronDown
              :class="{ 'rotate-180': openFilters?.length }"
              filled
            />
          </h3>
        </button>

        <FilterItem
          v-for="filter in filters"
          :key="filter.slug"
          :filter
          :is-open="openFilters?.includes(filter.slug)"
        />

        <TheBtn
          :is-loading="catalogPageIsLoading"
          color="secondary"
          size="md"
          @click="clearFilters"
        >
          Сбросить
        </TheBtn>
      </div>
    </section>

    <ProductCardsGallery
      v-if="isNoOffers && recommendedOffers?.length"
      :offers="recommendedOffers"
      :recommendations-block-id="recommendationsBlock?.block_identifier"
      :title="`<h2>${recommendationsBlock?.title}</h2>`"
      class="!p-0"
      no-padding
    />

    <section
      v-else
      class="products-listing__content"
    >
      <section
        ref="controlsElement"
        :class="{
          'products-listing__controls--header-visible': isShowHeader,
          'products-listing__controls--sticky': isControlsSticky,
        }"
        class="products-listing__controls"
      >
        <Dropdown
          v-if="availableSortOptions.length"
          v-model="selectedSortOption"
          :options="availableSortOptions"
          class="order-1"
          option-label="title"
          @change="changeSort"
        />

        <section
          v-if="activeFilters.length"
          class="products-listing__active-filters"
        >
          <button
            v-for="filter in activeFilters"
            :key="filter.valueText"
            class="products-listing__filter-pill products-listing__filter-pill--active-filter"
            name="selected-filter-pill"
            title="Сбросить фильтр"
            @click="removeFilter(filter)"
          >
            {{ filter.valueText }}

            <SvgoCross
              :font-controlled="false"
              class="size-4"
              filled
            />
          </button>
        </section>

        <button
          v-if="filters?.length"
          :class="{ 'products-listing__mobile-filters-btn--active': activeFilters.length }"
          class="products-listing__mobile-filters-btn"
          @click="isShowMobileFilters = !isShowMobileFilters"
        >
          <span
            v-if="activeFilters.length"
            class="products-listing__active-filters-count"
          >
            {{ activeFilters.length }}
          </span>

          Фильтры

          <SvgoFilter filled />
        </button>

        <BaseModal
          v-if="filters?.length"
          v-model="isShowMobileFilters"
          :confirm-btn-text="`Показать (${pagination?.total})`"
          :is-loading="catalogPageIsLoading"
          cancel-btn-text="Сбросить"
          @cancel="clearFilters"
          @close="isShowMobileFilters = false"
          @confirm="isShowMobileFilters = false"
        >
          <div class="flex flex-col gap-y-2">
            <button @click="switchFiltersIsOpenState">
              <h3 class="flex items-center justify-start gap-2">
                Фильтры

                <SvgoChevronDown
                  :class="{ 'rotate-180': openFilters?.length }"
                  filled
                />
              </h3>
            </button>

            <FilterItem
              v-for="filter in filters"
              :key="filter.slug"
              :filter
              :is-open="openFilters?.includes(filter.slug)"
            />
          </div>
        </BaseModal>
      </section>

      <p
        :class="{ 'lg:hidden': filters?.length }"
        class="flex justify-start text-sm md:text-base"
      >
        {{ productsCountPluralized }}
      </p>

      <section
        v-if="combinedOffers.length"
        class="flex flex-col"
      >
        <section
          :class="{ 'products-listing__products-grid--without-filters': !filters?.length }"
          class="products-listing__products-grid"
        >
          <AppLink
            v-for="(offer, index) in combinedOffers"
            :key="offer.id"
            :to="getCardOffer(offer).uri"
            class="hover:text-black"
          >
            <ProductCard
              :id="offer.id"
              :offer="getCardOffer(offer)"
              class="!h-full"
              @click="sendOfferEventToDataLayer('select_item', getCardOffer(offer), index)"
              @select-offer="setSelectedCardOffer(offer.id, $event)"
            />
          </AppLink>
        </section>

        <TheBtn
          v-if="!isLastPage"
          :is-loading="catalogPageIsLoading"
          :size="isMobile ? 'md' : 'lg'"
          class="mt-10"
          @click="loadMore"
        >
          Показать еще
        </TheBtn>

        <PaginationBlock
          v-if="computedPagination"
          :pagination="computedPagination"
          class="mt-6"
        />
      </section>

      <section
        v-else
        class="my-20 mx-4 text-center"
      >
        Товаров с такими фильтрами не найдено
      </section>
    </section>
  </div>
</template>

<style lang="scss" scoped>
.products-listing {
  display: flex;
  flex-direction: column;
  gap: 16px;

  @include screen-lg {
    flex-direction: row;
  }

  &__filters {
    display: none;

    @include screen-lg {
      display: flex;
      flex-direction: column;
      gap: 20px;
      max-width: 230px;
      height: 100%;
    }

    @include screen-xl {
      max-width: 270px;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
  }

  &__controls {
    position: sticky;
    top: 0;
    z-index: 105;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: space-between;
    align-items: center;
    margin-right: -12px;
    margin-left: -12px;
    padding-right: 12px;
    padding-left: 12px;
    background-color: $black-5;
    transition:
      top 210ms linear,
      margin-bottom 180ms ease,
      padding 180ms ease;

    &--header-visible {
      top: var(--dynamic-header-height);
    }

    &--sticky {
      margin-bottom: -16px;
      padding-top: 8px;
      padding-bottom: 8px;

      &::before {
        content: '';
        position: absolute;
        top: -2px;
        right: 0;
        left: 0;
        height: 2px;
        background-color: $black-5;
      }
    }

    @include screen-sm {
      margin-right: -24px;
      margin-left: -24px;
      padding-right: 24px;
      padding-left: 24px;
    }

    @include screen-md {
      justify-content: flex-start;
    }

    @include screen-lg {
      position: static;
      z-index: auto;
      margin-right: 0;
      margin-bottom: 0;
      margin-left: 0;
      padding: 0;
      background-color: transparent;
    }
  }

  &__products-grid {
    display: grid;
    grid-template-rows: repeat(1, minmax(0, 1fr));
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 4px;

    @include screen-sm {
      gap: 8px;
    }

    @include screen-md {
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 12px;
    }

    @include screen-xl {
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 16px;
    }

    &--without-filters {
      @include screen-lg {
        grid-template-columns: repeat(4, minmax(0, 1fr));
      }

      @include screen-xl {
        grid-template-columns: repeat(5, minmax(0, 1fr));
      }
    }
  }

  &__active-filters {
    display: flex;
    flex-grow: 1;
    gap: 8px;
    align-items: center;
    order: 3;
    overflow: auto;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__active-filters-count {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 20px;
    height: 20px;
    border-radius: 999px;
    background-color: $accent;
    color: $white;
    font-size: $text-xs;
  }

  &__filter-pill {
    display: flex;
    flex-shrink: 0;
    gap: 2px;
    align-items: center;
    border-radius: 8px;
    font-size: $text-xs;
    line-height: 1;
    transition: all 0.2s;

    &:hover {
      background-color: $black-10;
      color: $black-60;
    }

    &--active-filter {
      padding: 4px 6px 4px 8px;
      background-color: $black-20;
    }

    &--fast-filter {
      padding: 6px 10px;
      background-color: $black-10;
    }
  }

  &__mobile-filters-btn {
    display: flex;
    gap: 4px;
    align-items: center;
    order: 2;
    font-size: $text-sm;
    transition: all 0.2s;

    @include screen-md {
      order: 4;
      font-size: $text-base;
    }

    @include screen-lg {
      display: none;
    }

    &:hover {
      background-color: $black-5;
      color: $black-60;
    }

    &--active {
      padding: 4px 6px 4px 8px;
      border-radius: 8px;
      background-color: $black-10;
    }
  }
}
</style>
